import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  RouteProps,
  Switch,
  useHistory,
} from 'react-router-dom';
import './styles/styles.scss';
import BoxesComponent from './pages/Boxes';
import RemovalsComponent from './pages/Removals';
import RemovalsQuestionsComponent from './pages/RemovalsQuestions';
import RemovalsExtrasComponent from './pages/RemovalsExtras';
import CollectionDeliveryComponent from './pages/CollectionDelivery';
import PaymentComponent from './pages/Payment';
import BackToPaymentComponent from './pages/BackToPayment';
import SuccessComponent from './pages/Success';
import LandingComponent from './pages/Landing';
import LoginComponent from './pages/Login';
import RegistrationComponent from './pages/Register';
import ForgotPasswordComponent from './pages/ForgotPassword';
import ResetPasswordComponent from './pages/ResetPassword';
import UserDashboardComponent from './pages/User/Dashboard';
import UserOrderComponent from './pages/User/Order';
import UserOrderEditComponent from './pages/User/OrderEdit';
import UserProfileComponent from './pages/User/Profile';
import UserPaymentsComponent from './pages/User/Payments';
import UserUpdatePaymentComponent from './pages/User/UpdatePayment';
import UserPastOrdersComponent from './pages/User/PastOrders';
import AdminDashboardComponent from './pages/Admin/Dashboard';
import AdminUserComponent from './pages/Admin/User';
import AdminUsersListComponent from './pages/Admin/Users';
import AdminUsersCreateComponent from './pages/Admin/UsersCreate';
import AdminOrderComponent from './pages/Admin/Order';
import AdminOrderEditComponent from './pages/Admin/OrderEdit';
import AdminCustomerComponent from './pages/Admin/Customer';
import AdminCustomersComponent from './pages/Admin/Customers';
import AdminDiscountComponent from './pages/Admin/Discounts';
import AdminProductComponent from './pages/Admin/Product';
import AdminProductsComponent from './pages/Admin/Products';
import AdminRemovalQuoteDetailComponent from './pages/Admin/RemovalQuoteDetail';
import AdminLogisticsComponent from './pages/Admin/Logistics';
import AdminPartnersComponent from './pages/Admin/Partners';
import AdminInvoicesComponent from './pages/Admin/Invoices';
import Header from './components/Header';
import TrustPilotWidget from './components/TrustPilotWidget';
import UserContext, { User, UserState } from './contexts/UserContext';
import { ThemeSwitcher } from './components/ThemeSwitcher';
import { axiosKK } from './services/networkRequest';
import { Role } from './utilities/Role';
import { RouteComponentProps } from 'react-router';
import useGTM from '@elgorditosalsero/react-gtm-hook';
import { jwtDecode } from 'jwt-decode';
import OxfordPackagesComponent from './pages/OxfordPackages';
import Footer from './components/Footer';
import CanonicalTag from './components/CanonicalTag';
import useGtmEventTracking from './components/useGtmEventTracking';
import { HelmetProvider } from 'react-helmet-async';
import KlarnaReturnComponent from './pages/KlarnaReturn';

const loginPath = '/login';
const adminDashboardPath = '/admin/dashboard';

function LoginRedirect() {
  return <Redirect to={loginPath} />;
}

function AdminRedirect() {
  return <Redirect to={adminDashboardPath} />;
}

interface PrivateRouteProps extends RouteProps {
  user?: User;
  redirectComponent:
    | React.ComponentType<RouteComponentProps<any>>
    | React.ComponentType<any>;
  roles?: string[];
}

interface NoReturnRouteProps extends RouteProps {
  redirectPath: string;
}

function PrivateRoute({
  component: Component,
  user,
  redirectComponent,
  roles,
  ...props
}: PrivateRouteProps) {
  let allowed = user && (!roles || roles.includes(user.role));

  return (
    <Route {...props} component={allowed ? Component : redirectComponent} />
  );
}

function NoReturnRoute({
  component: Component,
  redirectPath,
  ...props
}: NoReturnRouteProps) {
  let allowed = useHistory().action !== 'POP';

  return (
    <Route
      {...props}
      component={allowed ? Component : () => <Redirect to={redirectPath} />}
    />
  );
}

function App() {
  let user: User | undefined;
  const userString = localStorage.getItem('user');

  if (userString) {
    try {
      user = JSON.parse(userString);

      if (user?.token) {
        const decodedUser = jwtDecode(user.token);
        const now = Date.now();
        const exp = (decodedUser.exp ?? 0) * 1000;

        if (exp < now) {
          localStorage.removeItem('user');
          user = undefined;
        } else {
          axiosKK.defaults.headers.common['Authorization'] =
            'bearer ' + user.token;
        }
      } else {
        localStorage.removeItem('user');
        user = undefined;
      }
    } catch (error) {
      // do nothing
    }
  }

  const { UseGTMHookProvider } = useGTM();
  // Initialize GTM using custom hook with error handling
  useGtmEventTracking();

  const history = useHistory();
  const [userState, setUserState] = useState<UserState>({
    updateUser: () => {},
    user,
  });
  useEffect(() => {
    setUserState((u) => ({
      ...u,
      updateUser: (user: User) => {
        setUserState((u2) => ({
          ...u2,
          user,
        }));
      },
    }));

    // Load Google Maps script
    const mapScriptId = 'google-maps';
    const mapScript = document.getElementById(mapScriptId);
    if (!mapScript) {
      const script = document.createElement('script');
      script.id = mapScriptId;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_MAPS_API_KEY}&libraries=places`;
      document.body.appendChild(script);
    }

    // Load Tawk.to chat widget (Kit Keeper flow only)
    const tawkScriptId = 'tawk-to-widget';
    const tawkScript = document.getElementById(tawkScriptId);
    const tawkPropertyId = process.env.REACT_APP_TAWK_PROPERTY_ID;
    const tawkWidgetId = process.env.REACT_APP_TAWK_WIDGET_ID;
    const storedFlow = localStorage.getItem('flow');
    // Show tawk.to for Kit Keeper flow only (not Keepr or St Annes)
    const isKitKeeperFlow = storedFlow !== 'Keepr' && storedFlow !== 'St Annes';

    if (!tawkScript && tawkPropertyId && tawkWidgetId && isKitKeeperFlow) {
      const script = document.createElement('script');
      script.id = tawkScriptId;
      script.async = true;
      script.src = `https://embed.tawk.to/${tawkPropertyId}/${tawkWidgetId}`;
      script.charset = 'UTF-8';
      script.setAttribute('crossorigin', '*');

      document.body.appendChild(script);
    } else if (tawkScript && !isKitKeeperFlow) {
      // Remove tawk.to widget if user switches to Keepr or St Annes flow
      document.body.removeChild(tawkScript);
      // Also remove tawk.to iframe if it exists
      const tawkWidget = document.getElementById('tawk-bubble');
      if (tawkWidget) {
        tawkWidget.remove();
      }
    }

    // Load Hotjar tracking script
    const hotjarScriptId = 'hotjar-tracking';
    const hotjarScript = document.getElementById(hotjarScriptId);
    if (!hotjarScript) {
      const hjid = storedFlow === 'Keepr' ? 6472223 : 6604580;
      const w = window as any;
      w.hj = w.hj || function () { (w.hj.q = w.hj.q || []).push(arguments); };
      w._hjSettings = { hjid, hjsv: 6 };
      const script = document.createElement('script');
      script.id = hotjarScriptId;
      script.async = true;
      script.src = `https://static.hotjar.com/c/hotjar-${hjid}.js?sv=6`;
      document.head.appendChild(script);
    }
  }, []);

  return (
    <HelmetProvider>
    <Router>
      <UserContext.Provider value={userState}>
        <UseGTMHookProvider>
          <ThemeSwitcher />
          <CanonicalTag />
          <div className="App">
            <Header />
            <TrustPilotWidget />
            <section className="App-white-box">
              <Switch>
                <Route
                  exact={true}
                  component={() => (
                    <LoginComponent
                      postLogin={(user) => {
                        setUserState({
                          ...userState,
                          user,
                        });
                      }}
                    />
                  )}
                  path={loginPath}
                />

                <Route
                  exact={true}
                  component={() => (
                    <RegistrationComponent
                      postRegistration={() => history.push('/dashboard')}
                      path="/register"
                      createAdminAccount={false}
                      setUserState={setUserState}
                      userState={userState}
                    />
                  )}
                  path="/register"
                />
                <Route
                  exact={true}
                  component={ForgotPasswordComponent}
                  path="/forgot-password"
                />
                <Route
                  exact={true}
                  component={ResetPasswordComponent}
                  path="/password-reset/:token"
                />
                <Route exact={true} component={LandingComponent} path="/" />
                <Route
                  exact={true}
                  component={OxfordPackagesComponent}
                  path="/oxford-packages"
                />
                <Route
                  exact={true}
                  component={RemovalsComponent}
                  path="/removals"
                />
                <Route
                  exact={true}
                  component={RemovalsQuestionsComponent}
                  path="/removals/questions"
                />
                <Route
                  exact={true}
                  component={RemovalsExtrasComponent}
                  path="/removals/extras"
                />
                <Route
                  exact={true}
                  component={BoxesComponent}
                  path="/:route(storage|shipping|suitcase)/boxes"
                />
                <Route
                  exact={true}
                  component={BoxesComponent}
                  path="/:route(storage)/boxes/packages"
                />

                {/* Combined CollectionDelivery component replacing the separate ones */}
                <Route
                  exact={true}
                  component={CollectionDeliveryComponent}
                  path="/:route(storage|shipping|suitcase)/collection_address"
                />
                <Route
                  exact={true}
                  component={CollectionDeliveryComponent}
                  path="/removals/collection_address"
                />

                {/* Note: Confirmation route is removed */}

                <Route
                  exact={true}
                  component={PaymentComponent}
                  path="/:route(storage|shipping|suitcase)/payment"
                />
                <Route
                  exact={true}
                  component={PaymentComponent}
                  path="/:route(storage|shipping|suitcase)/payment/3ds-secure"
                />
                <Route
                  exact={true}
                  component={BackToPaymentComponent}
                  path="/:route(storage|shipping|suitcase)/back-to-payment"
                />
                <Route
                  exact={true}
                  component={PaymentComponent}
                  path="/removals/payment"
                />
                <Route
                  exact={true}
                  component={PaymentComponent}
                  path="/removals/payment/3ds-secure"
                />
                <Route
                  exact={true}
                  component={BackToPaymentComponent}
                  path="/removals/back-to-payment"
                />

                <Route
                  exact={true}
                  component={KlarnaReturnComponent}
                  path="/:route(storage|shipping|suitcase)/klarna-return"
                />
                <Route
                  exact={true}
                  component={KlarnaReturnComponent}
                  path="/removals/klarna-return"
                />
                <Route
                  exact={true}
                  component={KlarnaReturnComponent}
                  path="/oxford-packages/klarna-return"
                />

                <NoReturnRoute
                  exact={true}
                  redirectPath="/dashboard"
                  component={SuccessComponent}
                  path="/:route(storage|shipping|suitcase|stannes)/success"
                />
                <NoReturnRoute
                  exact={true}
                  redirectPath="/dashboard"
                  component={SuccessComponent}
                  path="/removals/success"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={LoginRedirect}
                  component={UserDashboardComponent}
                  path="/dashboard"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={LoginRedirect}
                  component={UserPastOrdersComponent}
                  path="/past-orders"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={LoginRedirect}
                  component={UserOrderComponent}
                  path="/order/:id"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={LoginRedirect}
                  component={UserOrderComponent}
                  path="/removals/order/:id"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={LoginRedirect}
                  component={UserOrderEditComponent}
                  path="/order/:id/edit"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={LoginRedirect}
                  component={UserProfileComponent}
                  path="/profile"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={LoginRedirect}
                  component={UserPaymentsComponent}
                  path="/payments"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={LoginRedirect}
                  component={UserUpdatePaymentComponent}
                  path="/update-payment-method"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin, Role.Warehouse]}
                  component={AdminDashboardComponent}
                  path="/admin/dashboard"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminUserComponent}
                  path="/admin/user/:id"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminUsersListComponent}
                  path="/admin/users"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminUsersCreateComponent}
                  path="/admin/users/create"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin, Role.Warehouse]}
                  component={AdminOrderComponent}
                  path="/admin/order/:id"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin, Role.Warehouse]}
                  component={AdminRemovalQuoteDetailComponent}
                  path="/admin/removals/quote/:id"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminOrderEditComponent}
                  path="/admin/order/:id/edit"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin, Role.Warehouse]}
                  component={AdminCustomersComponent}
                  path="/admin/customers"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin, Role.Warehouse]}
                  component={AdminCustomerComponent}
                  path="/admin/customer/:id"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminDiscountComponent}
                  path="/admin/discounts"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminProductComponent}
                  path="/admin/product/:id"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminProductsComponent}
                  path="/admin/products"
                />
                {/* Consolidated Logistics page — single tabbed view covering
                    Orders / Route Plan / Warehouse / Forecast. Legacy paths
                    resolve to the right tab inside the page. */}
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminLogisticsComponent}
                  path="/admin/logistics"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminLogisticsComponent}
                  path="/admin/logistics/route-plan"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminLogisticsComponent}
                  path="/admin/logistics/warehouse"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminLogisticsComponent}
                  path="/admin/logistics/forecast"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminLogisticsComponent}
                  path="/admin/route-plan"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminLogisticsComponent}
                  path="/admin/warehouse"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminLogisticsComponent}
                  path="/admin/warehouse-forecast"
                />

                {/* Consolidated Partners page — single tabbed view covering
                    Partners list / User Access / per-partner Pricing / Vehicles
                    / Service Areas. Legacy paths resolve to the right tab. */}
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminPartnersComponent}
                  path="/admin/partner-access"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminPartnersComponent}
                  path="/admin/partners"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminPartnersComponent}
                  path="/admin/partners/:partnerId/pricing"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminPartnersComponent}
                  path="/admin/partners/:partnerId/vehicles"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminPartnersComponent}
                  path="/admin/partners/:partnerId/service-areas"
                />
                <PrivateRoute
                  user={userState.user}
                  exact={true}
                  redirectComponent={AdminRedirect}
                  roles={[Role.Admin]}
                  component={AdminInvoicesComponent}
                  path="/admin/invoices"
                />
                <Route exact={true} component={NotFound} />
              </Switch>
            </section>
            <Footer />
          </div>
        </UseGTMHookProvider>
      </UserContext.Provider>
    </Router>
    </HelmetProvider>
  );
}

function NotFound(): JSX.Element {
  return <h2>Unknown</h2>;
}

export default App;
